import * as React from 'react'
import { CompanyGroupModel } from '../types/companyGroups'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import useCompanyGroupService from '../services/CompanyGroupService'
import { activateCompany } from '@/app/actions/company'

type GroupType = 'custom' | 'default' | null

type EditCompanyGroupsContextType = {
  companiesInGroup: string[]
  selectedGroup: string[]
  isSelectedAny: boolean
  addedIds: string[]
  removedIds: string[]
  toggleAll: (checked: boolean) => void
  toggleId: (id: string) => void
  confirmEditGroupMembers: () => Promise<void>
  loading: boolean
  setGroupType: React.Dispatch<GroupType>
}

const EditCompanyGroupsContext = React.createContext<
  EditCompanyGroupsContextType | undefined
>(undefined)

interface EditCompanyGroupsProviderProps {
  children: React.ReactNode
  companyId: string
  group: CompanyGroupModel
  activateCompanyAction: (companyIds: string[], isActive: boolean) => Promise<any>
}

const EditCompanyGroupsProviderComponent: React.FC<EditCompanyGroupsProviderProps> = ({
  children,
  companyId,
  group,
  activateCompanyAction,
}) => {
  const { t } = useLocalize()
  const { getCompaniesInGroupById, addCompanyToGroup, removeCompanyFromGroup } = useCompanyGroupService()
  const [loading, setLoading] = React.useState(false)
  const [companiesInGroup, setCompaniesInGroup] = React.useState<string[]>([])
  const [selectedGroup, setSelectedGroup] = React.useState<string[]>([])
  const [initialIds, setInitialIds] = React.useState<string[]>([])
  const [groupType, setGroupType] = React.useState<GroupType>(null)

  const addedIds = React.useMemo(
    () => selectedGroup.filter((id) => !initialIds.includes(id)),
    [selectedGroup, initialIds],
  )

  const removedIds = React.useMemo(
    () => initialIds.filter((id) => !selectedGroup.includes(id)),
    [selectedGroup, initialIds],
  )

  const fetchCompaniesInGroup = async () => {
    setLoading(true)
    try {
      const ids = await getCompaniesInGroupById({
        companyId: companyId,
        groupId: group.id,
      })
      setCompaniesInGroup(ids)
      setInitialIds(ids)
    } catch (err) {
      toast.error({ text: t('addCompany.fetchCompaniesError') })
    } finally {
      setLoading(false)
    }
  }

  const toggleId = (id: string) => {
    setSelectedGroup((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id)
      }
      return [...prev, id]
    })
  }

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedGroup(companiesInGroup)
    } else {
      setSelectedGroup([])
    }
  }

  const confirmEditGroupMembers = async () => {
    setLoading(true)
    const isAnyAdded = addedIds.length > 0
    const isAnyRemoved = removedIds.length > 0

    if (!isAnyAdded && !isAnyRemoved) {
      toast.info({ text: t('addCompany.noChangesToSave') })
      setLoading(false)
      return
    }

    if (!groupType) {
      toast.error({ text: t('addCompany.unknownGroupType') })
      setLoading(false)
      return
    }

    try {
      if (groupType === 'custom') {
        const groupId = group.id
        if (isAnyRemoved) {
          await removeCompanyFromGroup({
            groupId: groupId,
            companies: removedIds,
            companyId: companyId,
          })
        }
        if (isAnyAdded) {
          await addCompanyToGroup({
            groupId: groupId,
            companies: addedIds,
            companyId: companyId,
          })
        }
      } else if (groupType === 'default') {
        // For default groups, use the activateCompany action
        // Group ID -1 = Active, Group ID -2 = Inactive
        const shouldActivate = group.id === -1

        if (isAnyAdded) {
          await activateCompanyAction(
            addedIds.map((id) => String(id)),
            shouldActivate,
          )
        }
        if (isAnyRemoved) {
          // Removing from Active group = deactivate
          // Removing from Inactive group = activate
          await activateCompanyAction(
            removedIds.map((id) => String(id)),
            !shouldActivate,
          )
        }
      }
      toast.success({ text: t('addCompany.groupMembersUpdated') })
    } catch (err) {
      toast.error({ text: t('addCompany.groupMembersUpdateError') })
    } finally {
      setLoading(false)
      await fetchCompaniesInGroup()
    }
  }

  React.useEffect(() => {
    if (group && group.id > 0) {
      setGroupType('custom')
    } else if (group && group.id < 0) {
      setGroupType('default')
    }
  }, [group?.id])

  React.useEffect(() => {
    if (companyId && group) {
      fetchCompaniesInGroup()
    }
  }, [companyId, group])

  React.useEffect(() => {
    setSelectedGroup(companiesInGroup.map((c) => c))
  }, [companiesInGroup])

  const value = React.useMemo(() => {
    const isSelectedAny = selectedGroup.length > 0

    return {
      loading,
      companiesInGroup,
      selectedGroup,
      isSelectedAny,
      addedIds,
      removedIds,
      toggleId,
      toggleAll,
      confirmEditGroupMembers,
      setGroupType,
    }
  }, [
    loading,
    companiesInGroup,
    selectedGroup,
    addedIds,
    removedIds,
    confirmEditGroupMembers,
    setGroupType,
  ])

  return (
    <EditCompanyGroupsContext.Provider value={value}>
      {children}
    </EditCompanyGroupsContext.Provider>
  )
}

export const EditCompanyGroupsProvider: React.FC<any> = (props) => (
  <EditCompanyGroupsProviderComponent
    {...props}
    activateCompanyAction={activateCompany}
  />
)

export const useEditCompanyGroups = (): EditCompanyGroupsContextType => {
  const ctx = React.useContext(EditCompanyGroupsContext)
  if (!ctx) {
    throw new Error(
      'useEditCompanyGroups must be used within a EditCompanyGroupsProvider',
    )
  }
  return ctx
}
